import { Injectable } from '@angular/core';
import { defaultIfEmpty, filter, forkJoin, map, mergeMap, of, switchMap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { UserStoreService } from '@auth/store/services/user-store.service';
import { CreateNotificationApiService } from '../services/create-notification-api.service';
import * as profileActions from '@profile/store/actions/profile.actions';
import { ProfileBaseStoreService } from 'app/modules/user-modules/profile-modules/profile-base/store/services/profile-base-store.service';
import * as profileBaseActions from 'app/modules/user-modules/profile-modules/profile-base/store/actions/profile-base.actions';
import * as loginActions from '@auth/store/actions/login.actions';
import * as registrationActions from '@auth/store/actions/invitation.actions';

@Injectable()
export class CreateUserNotificationEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly createNotificationApiService: CreateNotificationApiService,
        private readonly userStoreService: UserStoreService,
        private readonly profileBaseStoreService: ProfileBaseStoreService,
    ) { }

    public readonly createPrimaryCustomerDeactivatedNotification$ = createEffect(() => this.actions$.pipe(
        ofType(profileActions.deactivateAccountSuccessful),
        concatLatestFrom(() => [this.userStoreService.isPrimaryCustomer$, this.profileBaseStoreService.activeAdditionalProfiles$]),
        switchMap(([, isPrimaryCustomer, activeAdditionalProfiles]) => {
            return !isPrimaryCustomer
                ? of(loginActions.logout(false))
                : forkJoin(activeAdditionalProfiles.map(x => this.createNotificationApiService.createUserDeactivatedNotification(x.customerId)))
                    .pipe(defaultIfEmpty([]));
        }),
        map(x => loginActions.logout(false))
    ));

    public readonly createChildCustomerDeactivatedNotification$ = createEffect(() => this.actions$.pipe(
        ofType(profileBaseActions.deactivateChildAccountByPrimarySuccessful),
        mergeMap(({ childCustomerId }) => this.createNotificationApiService.createUserDeactivatedNotification(childCustomerId))
    ));

    public readonly createCustomerInvitedChildCustomerNotification$ = createEffect(() => this.actions$.pipe(
        ofType(profileBaseActions.inviteUserSuccessful),
        concatLatestFrom(() => [this.userStoreService.customerCollaborationId$, this.userStoreService.getUser(), this.userStoreService.customerFullName$]),
        switchMap(([{ childEmail }, collaborationId, { agentId, customerId }, customerName]) => {
            const input = {
                agentId,
                collaborationId,
                customerId,
                customerName,
                childCustomerName: childEmail
            };

            return this.createNotificationApiService.createCustomerInvitedChildCustomerNotification(input);
        })
    ));

    public readonly createPrimaryCustomerRegisteredNotification$ = createEffect(() => this.actions$.pipe(
        ofType(registrationActions.registrationSuccessful),
        concatLatestFrom(() => [this.userStoreService.isPrimaryCustomer$, this.userStoreService.customerFullName$]),
        filter(([, isPrimaryCustomer]) => isPrimaryCustomer),
        switchMap(([{ customerCollaborationId, agentId, customerId }, , customerName]) => {
            const input = {
                collaborationId: Number(customerCollaborationId),
                agentId,
                customerId,
                customerName
            };

            return this.createNotificationApiService.createPrimaryCustomerRegisteredNotification(input);
        })
    ));

    public readonly createChildCustomerRegisteredNotification$ = createEffect(() => this.actions$.pipe(
        ofType(registrationActions.registrationSuccessful),
        concatLatestFrom(() => [this.userStoreService.isPrimaryCustomer$, this.userStoreService.customerFullName$]),
        filter(([, isPrimaryCustomer]) => !isPrimaryCustomer),
        switchMap(([{ customerCollaborationId, agentId, customerId, primaryCustomerId }, , childCustomerName]) => {
            const input = {
                collaborationId: Number(customerCollaborationId),
                agentId,
                childCustomerId: customerId,
                childCustomerName,
                primaryCustomerId
            };

            return this.createNotificationApiService.createChildCustomerRegisteredNotification(input);
        })
    ));
}